import React from 'react'
import _ from 'lodash'
import { Col, Row } from 'react-bootstrap'
import GuestNumberPicker from '../guestNumberPicker'
import { connect } from 'react-redux'
import { BSP } from '../../constants/programConstants'
import CounterButton from '../counterButton'
import { defaultRoomValues } from '../../redux/formSchema'
import {
  autoAssignRoomIfNeeded,
  getNumAdults,
  getNumChildren,
  getNumGuestsFields,
  getNumRooms,
  getRoomConfigurations,
  getRoomId,
  getSelectedRoomIds,
  roomPickerOptionsSelector
} from '../../redux/modules/roomSelectionFormModule'
import {
  RoomConfigurationPicker,
  RoomPicker
} from './roomSelectionFormElements'
import { guestRequirementsSelector } from '../../redux/modules/searchParamsModule'

function getCommonRoomSelectorOptions(state, props) {
  const {
    roomListingField,
    rooms,
    index,
    listingIndex,
    authenticated,
    showConfigurationPicker,
    dispatch
  } = props
  const roomField = roomListingField.rooms[index]

  const roomIdField = roomField.roomId
  const roomId = getRoomId(roomField)
  const onRoomChange = roomIdField.onChange

  const roomConfigurationField = roomField.roomConfiguration
  const roomConfiguration = roomConfigurationField.value

  // Show room picker only if authenticated and a room configuration is selected
  const showRoomPicker =
    authenticated && (showConfigurationPicker ? roomConfiguration : true)

  const roomPickerOptions = roomPickerOptionsSelector(
    state,
    listingIndex,
    index
  )

  const onRoomConfigurationChange = value => {
    roomConfigurationField.onChange(value)
    dispatch(autoAssignRoomIfNeeded(listingIndex, index))
  }

  const numGuestsFields = getNumGuestsFields(
    showConfigurationPicker,
    roomField,
    onRoomConfigurationChange
  )

  return {
    roomConfiguration,
    onRoomConfigurationChange,
    ...(showConfigurationPicker && {
      roomConfigurations: getRoomConfigurations(
        rooms,
        getNumAdults(roomField),
        getNumChildren(roomField),
        getSelectedRoomIds(roomListingField),
        roomId
      )
    }),
    roomValue: roomId,
    onRoomChange,
    showRoomPicker,
    roomPickerOptions: roomPickerOptions,
    ...numGuestsFields,
    guestsField: roomField.guests,
    ...guestRequirementsSelector(state)
  }
}

const SingleRoomSelector = connect((state, props) => {
  return getCommonRoomSelectorOptions(state, props)
})(props => {
  let {
    guestsField,
    numGuestsField,
    maxGuests,
    showRoomPicker,
    roomPickerOptions,
    index,
    guestDetails,
    roomValue,
    onRoomChange
  } = props

  return (
    <div key={index}>
      {maxGuests > 1 && (
        <GuestNumberPicker
          numGuestsField={numGuestsField}
          guestsField={guestsField}
          guestDetails={guestDetails}
          maxValue={maxGuests}
        />
      )}
      {showRoomPicker && (
        <RoomPicker
          value={roomValue}
          options={roomPickerOptions}
          onChange={onRoomChange}
        />
      )}
    </div>
  )
})

const SingleRoomConfigurationSelector = connect((state, props) => {
  return getCommonRoomSelectorOptions(state, props)
})(props => {
  const {
    roomConfigurations,
    numAdultsField,
    numChildrenField,
    guestsField,
    guestDetails,
    roomValue,
    onRoomChange,
    roomPickerOptions,
    roomConfiguration,
    onRoomConfigurationChange,
    showRoomPicker,
    minAdultAge,
    childrenAllowed
  } = props
  const commonGuestPickerProps = {
    guestsField,
    guestDetails
  }

  /* Hardcode to age 7+ because minAdultAge was not showing correct */
  /* number. This will need to be changed for program bookings with */
  /* different ages. */
  return (
    <Row>
      <Col xs={6} md={3}>
        <GuestNumberPicker
          {...commonGuestPickerProps}
          label={
            <span style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
              Participants (Age 7+)
            </span>
          }
          numGuestsField={numAdultsField}
          maxValue={3}
        />
      </Col>
      {childrenAllowed && (
        <Col xs={6} md={4} lg={3}>
          <GuestNumberPicker
            {...commonGuestPickerProps}
            label={
              <span style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                Participants (Age 0-6)
              </span>
            }
            numGuestsField={numChildrenField}
            minValue={0}
          />
        </Col>
      )}
      <Col md={5} lg={6}>
        <RoomConfigurationPicker
          roomConfiguration={roomConfiguration}
          onRoomConfigurationChange={onRoomConfigurationChange}
          roomConfigurations={roomConfigurations}
        />
      </Col>

      {showRoomPicker && (
        <Col md={6}>
          <RoomPicker
            value={roomValue}
            options={roomPickerOptions}
            onChange={onRoomChange}
          />
        </Col>
      )}
    </Row>
  )
})

export const RoomSelectionForm = connect(
  (state, ownProps) => {
    const { roomListingField } = ownProps

    return {
      numRooms: getNumRooms(roomListingField)
    }
  },
  (dispatch, ownProps) => {
    return {
      onIncrementNumRooms: () => {
        const { roomListingField, index } = ownProps
        // This doesn't get updated between increment dispatch and
        // onIncrementNumRooms so we set it to next room index
        const nextRoomIndex = getNumRooms(roomListingField)
        roomListingField.rooms.addField(defaultRoomValues)
        dispatch(autoAssignRoomIfNeeded(index, nextRoomIndex))
      },
      onDecrementNumRooms: () => {
        const { roomListingField } = ownProps
        roomListingField.rooms.removeField()
      },
      onChangeNumRooms: (...args) => {
        const { roomListingField } = ownProps
        roomListingField.numRooms.onChange(...args)
      }
    }
  }
)(props => {
  let {
    program,
    roomListingField,
    numAvailable,
    disableAddingRooms,
    showConfigurationPicker,
    numRooms,
    guestDetails,
    rooms,
    maxGuests,
    showRoomPicker,
    onIncrementNumRooms,
    onDecrementNumRooms,
    onChangeNumRooms,
    authenticated,
    index: listingIndex,
    dispatch
  } = props

  const RoomSelectorComponent = showConfigurationPicker
    ? SingleRoomConfigurationSelector
    : SingleRoomSelector

  const roomSelectors =
    (maxGuests > 1 || showRoomPicker) &&
    _.times(numRooms, index => {
      return (
        <RoomSelectorComponent
          key={index}
          dispatch={dispatch}
          roomListingField={roomListingField}
          listingIndex={listingIndex}
          roomIndex={index}
          index={index} // Not actively used, use roomIndex instead
          guestDetails={guestDetails}
          rooms={rooms}
          maxGuests={maxGuests}
          showRoomPicker={showRoomPicker}
          authenticated={authenticated}
          showConfigurationPicker={showConfigurationPicker}
        />
      )
    })

  const roomCountButton = (
    <CounterButton
      label={
        program === BSP
          ? 'Please select one luggage spot per booking'
          : 'How many rooms do you require?'
      }
      testId={'btnGuestIncrement'}
      value={numRooms}
      minValue={0}
      maxValue={numAvailable}
      plusDisabled={disableAddingRooms}
      onIncrement={onIncrementNumRooms}
      onDecrement={onDecrementNumRooms}
      onChange={onChangeNumRooms}
    />
  )

  return showConfigurationPicker ? (
    <form>
      <Row>
        <Col md={4}>{roomCountButton}</Col>
        <Col md={8}>{roomSelectors}</Col>
      </Row>
    </form>
  ) : (
    <form>
      {roomCountButton}
      {roomSelectors}
    </form>
  )
})



// WEBPACK FOOTER //
// ./src/components/forms/roomSelectionForm.js